<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import NotificationBell from './NotificationBell.vue';
import LanguageMenu from './LanguageMenu.vue';
import AccountMenu from './AccountMenu.vue';
import { useAuth } from '../composables/useAuth.js';
import { useAppTheme } from '../composables/useAppTheme.js';

const { isSignedIn } = useAuth();
const { isDark, toggle } = useAppTheme();

/* Die Leiste steht fest oben. Solange man ganz oben ist, bleibt sie
   durchsichtig, damit der Grünschimmer hinter dem Wappen ungestört wirkt.
   Sobald Inhalt darunter durchläuft, bekommt sie einen Hintergrund — sonst
   liegt Text unlesbar hinter den Schaltern. */
const scrolled = ref(false);

function onScroll() {
  scrolled.value = window.scrollY > 12;
}

/* Höhe der Leiste als --topbar-h am Dokument: Die schmale Seitenleiste (PageBar.vue) klebt direkt darunter. */
const bar = ref(null);
let observer = null;
function publishHeight() {
  if (bar.value) document.documentElement.style.setProperty('--topbar-h', `${bar.value.offsetHeight}px`);
}

onMounted(() => {
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  publishHeight();
  if (typeof ResizeObserver !== 'undefined' && bar.value) {
    observer = new ResizeObserver(publishHeight);
    observer.observe(bar.value);
  }
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  observer?.disconnect();
});
</script>

<template>
  <div ref="bar" class="topbar" :class="{ scrolled }">
    <div class="tb-left">
      <NotificationBell v-if="isSignedIn" />
    </div>

    <div class="tb-right">
      <button class="login-status" :title="$t('settings.theme')" @click="toggle">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <template v-if="isDark">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </template>
          <template v-else>
            <circle cx="12" cy="12" r="4" />
            <path
              d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
              stroke-linecap="round"
            />
          </template>
        </svg>
      </button>

      <LanguageMenu />

      <AccountMenu v-if="isSignedIn" />
    </div>
  </div>
</template>

<style scoped>
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Unter dem Spieler-Panel (11) und dessen Abdunkler (10): Ist das Panel
     offen, soll die Leiste nicht darüberliegen. */
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  background: transparent;
  border-bottom: 1px solid transparent;
  transition: background 0.2s, border-color 0.2s, backdrop-filter 0.2s;
}
.topbar.scrolled {
  background: color-mix(in srgb, var(--bg) 82%, transparent);
  backdrop-filter: blur(10px);
  border-bottom-color: var(--border-soft);
}

.tb-left,
.tb-right {
  display: flex;
  align-items: center;
  gap: 8px;
  /* Damit der leere linke Bereich keine Klicks abfängt. */
  pointer-events: none;
}
.tb-left > *,
.tb-right > * {
  pointer-events: auto;
}

/* Glocke und Anmeldeanzeige positionieren sich im Original-Stylesheet selbst
   in den Bildschirmecken. Hier sitzen sie in einer Reihe.

   :deep() ist noetig, weil die Schalter inzwischen in eigenen Komponenten
   stecken (LanguageMenu, AccountMenu, NotificationBell) — die Zuordnung
   ueber den Bereichsschluessel dieser Datei erreicht sie sonst nicht. */
.topbar :deep(.notif-bell),
.topbar :deep(.login-status) {
  position: static;
  top: auto;
  left: auto;
  right: auto;
  color: var(--text);
  font-family: var(--sans);
  line-height: 1;
}
.topbar :deep(.login-status:hover),
.topbar :deep(.notif-bell:hover) {
  border-color: var(--green-dim);
}

/* Das Benachrichtigungsfeld hing am oberen linken Bildschirmrand; jetzt hängt
   es unter der Glocke. */
.topbar :deep(.notif-panel) {
  top: calc(100% + 6px);
  left: 0;
}

/* Desktop (ab 1024 px, wie die Desktop-Regeln in main.css): Die Glocke rückt zu den anderen
   Knöpfen nach rechts — gleiche Reihenfolge im Markup, nur die Leiste richtet sich rechts aus.
   Das Benachrichtigungsfeld hängt rechtsbündig unter der Glocke, ca. 380 px breit; links von
   der Glocke ist immer genug Platz, es wird nicht abgeschnitten. Handy bleibt unverändert. */
@media (min-width: 1024px) {
  .topbar {
    justify-content: flex-end;
  }
  .topbar :deep(.notif-panel) {
    left: auto;
    right: 0;
    width: 380px;
    max-width: calc(100vw - 32px);
  }
}
</style>
